import { AlternativeDetailsDialogViewModel } from '@Alternatives/Alternatives/Components/AlternativeDetailsDialog';
import AlternativeDetailsDialog from "@Alternatives/Alternatives/Components/AlternativeDetailsDialog.vue";
import { AttributeDescriptionDialogViewModel } from '@Alternatives/Attributes/Components/AttributeDescriptionDialog';
import AttributeDescriptionDialog from '@Alternatives/Attributes/Components/AttributeDescriptionDialog.vue';
import { isNullOrWhitespace, numCompare } from "@common/Common";
import { IEvaluationCommand } from '@Evaluation/cqrs';
import SurveyVoteEditor from '@Evaluation/Estimations/Components/SurveyVoteEditor.vue';
import { SavePanelistVotesCommandV1 } from '@Evaluation/Estimations/Domain/commands-core';
import EstimationsCommands from "@Evaluation/Estimations/Domain/commands-vue-component";
import EstimationsQueries from "@Evaluation/Estimations/Domain/queries-vue-component";
import { EstimationsSurveyQuestionReadModel, PanelistAlternativePageReadModel } from '@Evaluation/Estimations/Domain/readmodels';
import { EstimationsVote } from '@Evaluation/Model';
import { DiscreteScaleLevel } from '@Scales/Model';
import { Component, mixins } from 'vue-facing-decorator';
import { Route } from 'vue-router';

@Component({
    components: { SurveyVoteEditor, AttributeDescriptionDialog, AlternativeDetailsDialog },
    watch: {
        IsSaved: 'onIsSavedChanged'
    }
})
export default class PanelistAlternativePage extends mixins(EstimationsCommands, EstimationsQueries)
{
    get workspaceId() { return Number.parseInt(this.$route.params.workspaceId); }
    get surveyId(): number { return Number.parseInt(this.$route.params.surveyId); }
    get projectUserId(): number { return Number.parseInt(this.$route.params.projectUserId); }
    get alternativeId(): number { return Number.parseInt(this.$route.params.alternativeId); }

    Initialized: boolean = false;
    IsSaved: boolean = true;
    AutoSaveHandle: NodeJS.Timeout = null;

    DashboardUrl: string;
    AlternativeLabel: string;
    AlternativesLabel: string;
    NextAlternativeUrl: string;
    PrevAlternativeUrl: string;

    Name: string;
    Description: string;
    QuestionsCount: number;
    Questions: QuestionVM[] = [];
    SearchPhrase: string = null;
    QuestionsFilter: string = "All";

    attributeDescriptionDialog = new AttributeDescriptionDialogViewModel();
    alternativeDetailsDialog = new AlternativeDetailsDialogViewModel();

    get Progress(): number { return Math.round(100 * this.AnsweredQuestionsCount / this.QuestionsCount); }
    get ProgressStr(): string { return `${this.AnsweredQuestionsCount} / ${this.QuestionsCount}`; }

    get QuestionsView(): QuestionVM[]
    {
        let view = [...this.Questions];

        if (!isNullOrWhitespace(this.SearchPhrase))
        {
            const phrase = this.SearchPhrase.toLowerCase();
            view = view.filter(q => q.AttributeName.toLowerCase().indexOf(phrase) >= 0);
        }

        if (this.QuestionsFilter === "Answered")
        {
            view = view.filter(a => a.HasAnswer);
        }
        else if (this.QuestionsFilter === "Not answered")
        {
            view = view.filter(a => !a.HasAnswer);
        }
        else if (this.QuestionsFilter === "Has value")
        {
            view = view.filter(a => a.HasValue);
        }
        else if (this.QuestionsFilter === "No value")
        {
            view = view.filter(a => !a.HasValue);
        }
        else if (this.QuestionsFilter === "Commented")
        {
            view = view.filter(a => a.HasComment);
        }
        else if (this.QuestionsFilter === "Not commented")
        {
            view = view.filter(a => !a.HasComment);
        }

        return view.sort((a, b) => numCompare(a.OrderValue, b.OrderValue));
    }

    get AnsweredQuestionsCount(): number { return this.Questions.filter(a => a.HasAnswer).length; }
    get NotAnsweredQuestionsCount(): number { return this.QuestionsCount - this.AnsweredQuestionsCount; }
    get CommentedQuestionsCount(): number { return this.Questions.filter(a => a.HasComment).length; }
    get NotCommentedQuestionsCount(): number { return this.QuestionsCount - this.CommentedQuestionsCount; }
    get VotedQuestionsCount(): number { return this.Questions.filter(a => a.HasValue).length; }
    get NotVotedQuestionsCount(): number { return this.QuestionsCount - this.VotedQuestionsCount; }


    //watcher 'IsSaved'
    onIsSavedChanged(val: boolean, oldVal: boolean) 
    {
        if (val === false)
        {
            if (this.AutoSaveHandle == null)
            {
                this.AutoSaveHandle = setInterval(() =>
                {
                    if (this.IsSaved === false)
                    {
                        this.Save();
                    }

                }, 10000);
            }
        }
        else
        {
            if (this.AutoSaveHandle != null)
            {
                clearInterval(this.AutoSaveHandle);
                this.AutoSaveHandle = null;
            }
        }
    }


    SetQuestionsFilterCommand(filter: string)
    {
        this.QuestionsFilter = filter;
    }

    ShowAlternativeDetailsCommand()
    {
        this.alternativeDetailsDialog.show(this.Name, this.Description);
    }


    SetSaved(alternativeId: number)
    {
        if (this.alternativeId === alternativeId)
        {
            if (this.IsSaved == null)
            {
                this.IsSaved = true;
            }
        }
    }


    Save()
    {
        this.IsSaved = null;

        const votes = this.Questions.map(q => q.GetVote());

        this.invokeSavePanelistVotesCommandV1(this.workspaceId, this.surveyId, this.projectUserId, this.alternativeId, votes);
    }

    ScrollTopCommand()
    {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    ShowAttributeDetailsCommand(q: QuestionVM)
    {
        this.attributeDescriptionDialog.show(q.AttributeName, q.AttributeDescription);
    }

    handleVoteValueUpdate(q: QuestionVM, val: number | null)
    {
        q.Value = val;
        this.IsSaved = false;
    }

    handleVoteCommentUpdate(q: QuestionVM, comment: string | null)
    {
        q.Comment = comment;
        this.IsSaved = false;
    }


    mounted()
    {
        //document.title = "Panelists | TransparentChoice";
        this.runPanelistAlternativePageQueryV1(this.workspaceId, this.surveyId, this.projectUserId, this.alternativeId);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName == "PanelistAlternativePageReadModel")
        {
            const rm = model as PanelistAlternativePageReadModel;

            this.NextAlternativeUrl = rm.NextAlternativeUrl;
            this.PrevAlternativeUrl = rm.PrevAlternativeUrl;

            this.DashboardUrl = rm.DashboardUrl;
            this.AlternativeLabel = rm.AlternativeLabel;
            this.AlternativesLabel = rm.AlternativesLabel;

            this.Name = rm.AlternativeName;
            this.Description = rm.AlternativeDescription;
            this.Questions = rm.Questions.map(a => new QuestionVM(a));

            this.QuestionsCount = this.Questions.length;

            this.Initialized = true;
        }
    }

    onCommandSucceed(cmd: IEvaluationCommand, correlationId: string)
    {
        if (SavePanelistVotesCommandV1.matchType(cmd))
        {
            const alternativeId = correlationId;
            this.IsSaved = true;
        }
    }


    EnsureSavedOnExit(event: BeforeUnloadEvent)
    {
        if (this.IsSaved === true)
        {
            return;
        }

        this.Save();
        event.preventDefault();
        event.returnValue = "";
    }

    beforeMount()
    {
        window.addEventListener("beforeunload", this.EnsureSavedOnExit);
    }

    beforeRouteLeave(to: Route, from: Route, next: (arg?: any) => void)
    {
        if (this.IsSaved === false)
        {
            this.Save(); 
        }

        next();
    }

    beforeRouteUpdate(to: Route, from: Route, next: (arg?: any) => void)
    {
        if (this.IsSaved === false)
        {
            this.Save();
        }

        next();
    }

    beforeDestroy()
    {
        window.removeEventListener("beforeunload", this.EnsureSavedOnExit);

        if (this.AutoSaveHandle != null)
        {
            clearInterval(this.AutoSaveHandle);
            this.AutoSaveHandle = null;
        }
    }
}

class QuestionVM
{
    QuestionId: string;
    OrderValue: number;
    HasScale: boolean;
    ScaleLevels: DiscreteScaleLevel[];

    Comment: string = null;
    Value: number = null;

    AttributeName: string;
    AttributeDescription: string;

    get HasValue(): boolean
    {
        if (this.HasScale)
        {
            return this.Value != null;
        }
        else
        {
            if (this.Value == null)
            {
                return false;
            }

            if (isNullOrWhitespace(this.Value.toString()))
            {
                return false;
            }

            return true;
        }
    }
    get HasComment(): boolean { return !isNullOrWhitespace(this.Comment); }
    get HasAnswer(): boolean { return this.HasValue || this.HasComment; }

    constructor(rm: EstimationsSurveyQuestionReadModel)
    {
        this.QuestionId = rm.QuestionId;
        this.OrderValue = rm.OrderValue;
        this.HasScale = rm.Scale != null;
        this.ScaleLevels = rm.Scale;
        this.Value = rm.Value;
        this.Comment = rm.Comment;

        this.AttributeName = rm.AttributeName;
        this.AttributeDescription = rm.AttributeDescription;
    }

    GetValue(): number
    {
        if (this.HasScale)
        {
            return this.Value;
        }
        else
        {
            if (this.Value == null)
            {
                return null;
            }

            const strVal = this.Value.toString().replace(',', '.');

            if (isNullOrWhitespace(strVal))
            {
                return null;
            }

            if (!isNaN(Number(strVal)))
            {
                return Number(strVal);
            }

            return this.Value;
        }
    }

    GetVote(): EstimationsVote
    {
        const v = new EstimationsVote();
        v.Comment = this.Comment;
        v.Value = this.GetValue();
        v.QuestionId = this.QuestionId;
        return v;
    }
}